import type { CardType } from "@mn/shared";
import { Link } from "react-router";
import { cardIcon } from "@/board/icons";
import { useSession } from "@/app/session";
import { Shell } from "@/components/Shell";
import { useLocale } from "@/lib/i18n";

type Section = { id: string; title: string; body: (string | { card: CardType; text: string })[] };

const GUIDE: Record<"es" | "en", { title: string; intro: string; sections: Section[] }> = {
  es: {
    title: "Guía rápida",
    intro:
      "Todo lo que necesitas para trabajar en .MAYUSCULANOTE, en cinco minutos. Vuelve aquí cuando quieras desde el menú.",
    sections: [
      {
        id: "tableros",
        title: "Tableros y sub-tableros",
        body: [
          "Hay un solo espacio de trabajo, el de la agencia. Todo el equipo ve y edita todos los tableros; lo único personal son tus Notas rápidas.",
          "Inicio es el tablero raíz del equipo. Dentro de cada tablero puedes crear otros, como carpetas: Clientes → Marca → Campaña.",
          "Un tablero aparece como una ficha cuadrada. Doble clic en la ficha para abrirlo; las migas de pan arriba te regresan.",
          "Para crear uno: botón ▣ Tablero en la barra, o clic derecho en el lienzo → Nuevo tablero. Clic derecho en la ficha → Ajustes del tablero para nombre, ícono y color.",
          "Los clientes solo ven los tableros que les compartes, y todo lo que hay dentro de ellos.",
        ],
      },
      {
        id: "privados",
        title: "Tableros del equipo y privados",
        body: [
          "Inicio es del equipo: todo lo que pongas ahí lo ven y lo editan todos. Úsalo para lo compartido, no para trabajo personal.",
          "Cualquier otro tablero puede ser privado: clic derecho en su ficha → Ajustes del tablero → Visibilidad → Privado. Solo tú lo ves, con todos sus sub-tableros; los demás ven una ficha con candado sin nombre.",
          "Para que alguien concreto entre a tu tablero privado, invítalo desde Compartir, igual que a un cliente. Y para volverlo del equipo, cambia la visibilidad otra vez.",
          "Arriba, junto al nombre del tablero, un sello dice siempre si estás en uno del Equipo o Privado.",
        ],
      },
      {
        id: "tarjetas",
        title: "Tarjetas",
        body: [
          "Doble clic en un espacio vacío crea una nota. Arrastra imágenes, videos o archivos desde tu computadora y se suben solos. Pega un enlace y se convierte en tarjeta con vista previa.",
          {
            card: "note",
            text: "Nota: texto con formato (negrita, listas, colores). Doble clic para editar, Esc para terminar.",
          },
          { card: "heading", text: "Encabezado: títulos grandes para organizar el lienzo." },
          { card: "image", text: "Imagen: foto o gráfico. Se generan miniaturas automáticamente." },
          {
            card: "link",
            text: "Enlace: vista previa de cualquier página; YouTube, Vimeo y Loom se reproducen dentro.",
          },
          { card: "board", text: "Tablero: una ficha que abre otro tablero." },
          {
            card: "column",
            text: "Columna: agrupa tarjetas en una lista vertical; arrastra tarjetas dentro o fuera.",
          },
          { card: "todo", text: "Lista de tareas: pendientes con casillas." },
          { card: "comment", text: "Comentario: hilo de conversación; escribe @ para mencionar a alguien." },
          { card: "line", text: "Línea: selecciona dos tarjetas y conéctalas con una flecha." },
          { card: "swatch", text: "Color: una muestra con su código HEX." },
          { card: "sketch", text: "Boceto: dibuja a mano dentro de una tarjeta." },
          { card: "annotation", text: "Dibujo en el lienzo: trazos libres sobre el tablero." },
          { card: "table", text: "Tabla: celdas con fórmulas sencillas." },
          { card: "document", text: "Documento: texto largo que se abre en grande." },
          { card: "video", text: "Video y audio: se reproducen en la tarjeta." },
          { card: "file", text: "Archivo: PDF, PSD, AI o cualquier otro, con descarga." },
        ],
      },
      {
        id: "organizar",
        title: "Mover y organizar",
        body: [
          "Arrastra para mover. Al acercarte a otra tarjeta aparecen guías rosas para alinear. Shift + clic selecciona varias; arrastrar en el lienzo vacío selecciona por recuadro.",
          "Clic derecho sobre una selección: colores, etiquetas, reacciones, duplicar, alinear y distribuir, traer al frente, bloquear, enviar a la papelera.",
          "Si sueltas una tarjeta sobre otra, la de abajo se empuja hacia abajo (Evitar superposición, en el menú del lienzo).",
          "Arrastra tarjetas sobre la ficha de un tablero para moverlas a ese tablero: llegan a su panel Sin clasificar.",
          "Rueda del ratón para desplazarte, Ctrl + rueda para acercar, Z para ver todo el tablero. En el celular: un dedo mueve, dos dedos acercan, mantener presionado arrastra.",
        ],
      },
      {
        id: "compartir",
        title: "Compartir con clientes",
        body: [
          "Botón Compartir en un tablero: invita por correo con rol Solo ver, Comentar o Editar. El invitado recibe un enlace y entra con su correo, sin contraseña si prefiere.",
          "Publicar genera un enlace público (opcionalmente con contraseña) para quien no tenga cuenta.",
          "El equipo (Equipo en el menú) ve todo; los clientes solo lo compartido.",
        ],
      },
      {
        id: "avisos",
        title: "Comentarios y avisos",
        body: [
          "La campana muestra tus avisos, la actividad del tablero y quién está conectado.",
          "Te llega un correo cuando te mencionan o te comparten un tablero; los comentarios se agrupan en un resumen cada pocos minutos.",
          "En Ajustes puedes activar notificaciones en tu teléfono o computadora.",
        ],
      },
      {
        id: "herramientas",
        title: "Buscar, exportar, versiones",
        body: [
          "Buscar (Ctrl + /) encuentra tableros y texto dentro de las tarjetas.",
          "Exportar: PDF o PNG del tablero (oscuro o claro para imprimir), Word, Markdown, texto o un ZIP con los archivos.",
          "Historial: cada tablero guarda versiones automáticas; puedes volver a cualquiera. Lo que borres va a la Papelera.",
          "Presentar (P) recorre el tablero tarjeta por tarjeta en pantalla completa.",
          "Notas rápidas (✎ en el encabezado) es tu libreta personal; desde un tablero puedes enviarlas al lienzo.",
          "Plantillas: brief, moodboard, calendario, revisión de cliente, storyboard y plan de proyecto. Cualquier tablero se puede convertir en plantilla.",
        ],
      },
      {
        id: "atajos",
        title: "Atajos",
        body: ["Pulsa ? dentro de un tablero para ver la lista completa de atajos de teclado."],
      },
    ],
  },
  en: {
    title: "Quick guide",
    intro:
      "Everything you need to work in .MAYUSCULANOTE, in five minutes. Come back any time from the menu.",
    sections: [
      {
        id: "boards",
        title: "Boards and sub-boards",
        body: [
          "There is a single workspace, the agency's. The whole team sees and edits every board; the only personal area is your Quick Notes.",
          "Inicio is the team's root board. Inside any board you can create others, like folders: Clients → Brand → Campaign.",
          "A board shows up as a square tile. Double-click the tile to open it; the breadcrumbs at the top take you back.",
          "To create one: the ▣ Board button in the toolbar, or right-click the canvas → New board. Right-click the tile → Board settings for name, icon and colour.",
          "Clients only see the boards you share with them, and everything inside those.",
        ],
      },
      {
        id: "private",
        title: "Team and private boards",
        body: [
          "Inicio belongs to the team: everything you put there is seen and edited by everyone. Use it for shared work, not personal work.",
          "Any other board can be private: right-click its tile → Board settings → Visibility → Private. Only you see it, with all its sub-boards; others see a locked tile with no name.",
          "To let a specific person into your private board, invite them from Share, like a client. To give it back to the team, change the visibility again.",
          "At the top, next to the board name, a badge always says whether you are in a Team or a Private board.",
        ],
      },
      {
        id: "cards",
        title: "Cards",
        body: [
          "Double-click an empty spot to create a note. Drag images, videos or files from your computer and they upload on their own. Paste a link and it becomes a preview card.",
          {
            card: "note",
            text: "Note: formatted text (bold, lists, colours). Double-click to edit, Esc to finish.",
          },
          { card: "heading", text: "Heading: big titles to structure the canvas." },
          { card: "image", text: "Image: a photo or graphic. Thumbnails are generated automatically." },
          { card: "link", text: "Link: a preview of any page; YouTube, Vimeo and Loom play inline." },
          { card: "board", text: "Board: a tile that opens another board." },
          { card: "column", text: "Column: groups cards in a vertical list; drag cards in or out." },
          { card: "todo", text: "To-do list: tasks with checkboxes." },
          { card: "comment", text: "Comment: a conversation thread; type @ to mention someone." },
          { card: "line", text: "Line: select two cards and connect them with an arrow." },
          { card: "swatch", text: "Colour: a swatch with its HEX code." },
          { card: "sketch", text: "Sketch: draw by hand inside a card." },
          { card: "annotation", text: "Canvas drawing: free strokes over the board." },
          { card: "table", text: "Table: cells with simple formulas." },
          { card: "document", text: "Document: long text that opens large." },
          { card: "video", text: "Video and audio: play inside the card." },
          { card: "file", text: "File: PDF, PSD, AI or anything else, with download." },
        ],
      },
      {
        id: "organise",
        title: "Move and organise",
        body: [
          "Drag to move. Near another card, pink guides appear to help you align. Shift + click selects several; dragging on empty canvas selects by rectangle.",
          "Right-click a selection: colours, labels, reactions, duplicate, align and distribute, bring to front, lock, move to trash.",
          "Drop a card on top of another and the one below is pushed down (Prevent overlap, in the canvas menu).",
          "Drag cards onto a board tile to move them into that board: they land in its Unsorted panel.",
          "Mouse wheel scrolls, Ctrl + wheel zooms, Z fits the whole board. On the phone: one finger pans, two fingers zoom, press and hold to drag.",
        ],
      },
      {
        id: "share",
        title: "Sharing with clients",
        body: [
          "Share button on a board: invite by email with the role View only, Comment or Edit. The guest gets a link and signs in with their email, no password needed.",
          "Publish creates a public link (optionally with a password) for people without an account.",
          "The team (Team in the menu) sees everything; clients see only what is shared.",
        ],
      },
      {
        id: "notifications",
        title: "Comments and notifications",
        body: [
          "The bell shows your notifications, the board's activity and who is online.",
          "You get an email when you are mentioned or a board is shared with you; comments are grouped into a digest every few minutes.",
          "In Settings you can turn on notifications on your phone or computer.",
        ],
      },
      {
        id: "tools",
        title: "Search, export, versions",
        body: [
          "Search (Ctrl + /) finds boards and text inside cards.",
          "Export: PDF or PNG of the board (dark, or light for print), Word, Markdown, text or a ZIP with the files.",
          "History: every board keeps automatic versions; you can go back to any of them. Deleted things go to the Trash.",
          "Present (P) walks through the board card by card in full screen.",
          "Quick notes (✎ in the header) is your personal notepad; from a board you can send them onto the canvas.",
          "Templates: brief, moodboard, calendar, client review, storyboard and project plan. Any board can become a template.",
        ],
      },
      {
        id: "shortcuts",
        title: "Shortcuts",
        body: ["Press ? inside a board for the full list of keyboard shortcuts."],
      },
    ],
  },
};

const FEEDBACK_SECTION: Record<"es" | "en", Section> = {
  es: {
    id: "pruebas",
    title: "Fase de pruebas",
    body: [
      "Estamos probando .MAYUSCULANOTE con trabajo real. Si algo no funciona como esperabas, usa Reportar fallo (arriba a la derecha, o en el menú ⋯ en el teléfono).",
      "Cuenta qué hiciste y qué esperabas. Deja marcada la casilla de datos técnicos: incluye la página, el navegador y los últimos errores, que es lo que permite encontrar la causa.",
      "Tu trabajo siempre queda guardado en el servidor; reportar un fallo no cambia nada en los tableros.",
    ],
  },
  en: {
    id: "testing",
    title: "Testing phase",
    body: [
      "We are testing .MAYUSCULANOTE with real work. If something doesn't work as you expected, use Report a problem (top right, or in the ⋯ menu on a phone).",
      "Say what you did and what you expected. Keep the technical-details box ticked: it includes the page, the browser and the latest errors, which is what makes the cause findable.",
      "Your work is always saved on the server; reporting a problem changes nothing on the boards.",
    ],
  },
};

export const GUIDE_SEEN_KEY = (userId: string) => `mn_guide_seen_${userId}`;

/** Static, always-available user guide (es/en follows the language toggle). */
export default function Guide() {
  const locale = useLocale((s) => s.locale);
  const { me } = useSession();
  const base = GUIDE[locale];
  const g = me?.workspace.feedbackEnabled
    ? { ...base, sections: [...base.sections, FEEDBACK_SECTION[locale]] }
    : base;
  return (
    <Shell>
      <div className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="display mb-2 text-4xl">.{g.title}</h1>
        <p className="mb-6 text-muted">{g.intro}</p>
        <nav className="mb-8 flex flex-wrap gap-2 text-sm">
          {g.sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="rounded-sm border border-border px-2 py-1 text-muted no-underline hover:text-text"
            >
              {s.title}
            </a>
          ))}
        </nav>
        {g.sections.map((s) => (
          <section key={s.id} id={s.id} className="mb-10">
            <h2 className="display mb-3 border-b border-border pb-2 text-2xl">.{s.title}</h2>
            <div className="flex flex-col gap-3 text-[15px] leading-relaxed text-text">
              {s.body.map((b, i) =>
                typeof b === "string" ? (
                  <p key={`${s.id}-${i}`}>{b}</p>
                ) : (
                  <div key={`${s.id}-${b.card}`} className="flex items-start gap-3">
                    <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-panel text-muted">
                      {cardIcon(b.card, 18)}
                    </span>
                    <span>{b.text}</span>
                  </div>
                ),
              )}
            </div>
          </section>
        ))}
        <p className="text-sm text-muted">
          <Link to="/">{locale === "es" ? "Volver a los tableros" : "Back to the boards"}</Link>
        </p>
      </div>
    </Shell>
  );
}
